import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { Loader2, CheckCircle2, AlertCircle, Upload, FolderPlus } from "lucide-react";

const SAMPLE = `sub_portfolio,transaction_type,ticker,quantity,price,fees,tax_withheld,transaction_date,notes
TECH,BUY,MSFT,300,245.10,0,0,2023-01-15,
QuantPick,BUY,MSFT,250,280.00,0,0,2023-03-20,
TECH,DIVIDEND,MSFT,550,0.75,0,61.88,2024-06-13,Qtr dividend
Main,DEPOSIT,,1,10000,0,0,2024-01-05,Funding`;

export function ImportDialog({ open, onOpenChange, onImported }) {
    const [csv, setCsv] = useState("");
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dayfirst, setDayfirst] = useState("true");
    const [overrides, setOverrides] = useState({});
    const fileRef = useRef(null);

    const doPreview = async (ov = overrides) => {
        setLoading(true);
        try { const { data } = await api.post("/transactions/import/preview", { csv, dayfirst: dayfirst === "true", overrides: ov }); setPreview(data); }
        catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
    };

    const applySub = (row, sub) => {
        const ov = { ...overrides, [row]: { sub_portfolio: sub } };
        setOverrides(ov);
        doPreview(ov);
    };

    const onFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => { setCsv(String(reader.result || "")); setPreview(null); };
        reader.onerror = () => toast.error("Could not read that file");
        reader.readAsText(f);
    };

    const doCommit = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/transactions/import/commit", { csv, dayfirst: dayfirst === "true", overrides });
            const created = data.sub_portfolios_created || [];
            toast.success(`Imported ${data.inserted} transactions${created.length ? ` · created ${created.length} sub-portfolio${created.length > 1 ? "s" : ""}` : ""}`);
            if (data.skipped > 0) toast.info(`${data.skipped} row${data.skipped > 1 ? "s" : ""} skipped (invalid or not covered by shares held)`);
            if (data.open_issues) toast.warning(`${data.open_issues} new issue${data.open_issues > 1 ? "s" : ""} to review`, {
                description: "e.g. a ticker that has changed, or a leftover fraction after a sale.",
                action: { label: "Review", onClick: () => { window.location.href = "/issues"; } },
                duration: 12000,
            });
            onImported?.(); reset(); onOpenChange(false);
        } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
    };

    const reset = () => { setCsv(""); setPreview(null); setOverrides({}); setDayfirst("true"); if (fileRef.current) fileRef.current.value = ""; };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
            <DialogContent className="max-w-2xl" data-testid="import-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading">Import transactions from CSV</DialogTitle>
                    <DialogDescription>Upload a .csv file or paste rows with headers: sub_portfolio, transaction_type, ticker, quantity, price, fees, tax_withheld, transaction_date, notes. Use tax_withheld (not fees) for dividend withholding tax. Any new sub-portfolio is created automatically. We validate before importing.</DialogDescription>
                </DialogHeader>

                <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="import-file-input" />
                <Button variant="outline" onClick={() => fileRef.current?.click()} className="w-full" data-testid="import-file-btn">
                    <Upload className="w-4 h-4 mr-2" />Choose CSV file…
                </Button>
                <div className="text-center text-[11px] uppercase tracking-wider text-muted-foreground">or paste below</div>

                <Textarea value={csv} onChange={(e) => { setCsv(e.target.value); setPreview(null); setOverrides({}); }} rows={7}
                    placeholder={SAMPLE} className="num text-xs" data-testid="import-textarea" />

                <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
                    <div>
                        <Label className="text-sm">Day/month date order</Label>
                        <p className="text-xs text-muted-foreground">How to read numeric dates like 11/06/2024. ISO dates (2024-11-06) and month names are always read correctly.</p>
                    </div>
                    <Select value={dayfirst} onValueChange={(v) => { setDayfirst(v); setPreview(null); }}>
                        <SelectTrigger className="w-[190px] shrink-0" data-testid="import-dayfirst-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="true">Day first (DD/MM/YYYY)</SelectItem>
                            <SelectItem value="false">Month first (MM/DD/YYYY)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {preview && (
                    <div className="space-y-2 max-h-52 overflow-y-auto text-xs">
                        <div className="flex gap-3 flex-wrap">
                            <Badge className="bg-gain/15 text-gain border-gain/30"><CheckCircle2 className="w-3 h-3 mr-1" />{preview.valid.length} valid</Badge>
                            {preview.invalid.length > 0 && <Badge className="bg-loss/15 text-loss border-loss/30"><AlertCircle className="w-3 h-3 mr-1" />{preview.invalid.length} invalid</Badge>}
                            {(preview.new_sub_portfolios || []).length > 0 && (
                                <Badge variant="outline" className="border-primary/40 text-primary" data-testid="import-new-subs">
                                    <FolderPlus className="w-3 h-3 mr-1" />New: {preview.new_sub_portfolios.join(", ")}
                                </Badge>
                            )}
                        </div>
                        {preview.invalid.map((r) => (
                            <div key={r.row} className="p-2 rounded bg-loss/10 border border-loss/20 flex items-center justify-between gap-2" data-testid={`import-invalid-row-${r.row}`}>
                                <span><span className="font-semibold">Row {r.row}</span> ({r.data.transaction_type} {r.data.ticker}, {r.data.sub_portfolio}): {r.errors.join(", ")}</span>
                                {r.suggested_sub && (
                                    <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" disabled={loading}
                                        onClick={() => applySub(r.row, r.suggested_sub)} data-testid={`import-use-sub-${r.row}`}>
                                        Use {r.suggested_sub}
                                    </Button>
                                )}
                            </div>
                        ))}
                        {preview.invalid.some((r) => r.errors.some((e) => e.startsWith("SELL not covered"))) && (
                            <p className="text-muted-foreground" data-testid="import-uncovered-note">Uncovered sales are skipped. Fix the sub-portfolio here, or correct the file and preview again.</p>
                        )}
                        {preview.valid.length > 0 && (
                            <div className="pt-1" data-testid="import-date-preview">
                                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Dates will be saved as</div>
                                <div className="space-y-0.5">
                                    {preview.valid.slice(0, 6).map((r) => (
                                        <div key={r.row} className="flex justify-between gap-3 border-b border-border/40 py-0.5">
                                            <span className="num">{r.data.ticker || r.data.transaction_type}</span>
                                            <span className="text-muted-foreground num">{r.data.transaction_date}</span>
                                            <span className="font-medium">{fmtDate(r.data.transaction_date)}</span>
                                        </div>
                                    ))}
                                </div>
                                {preview.valid.length > 6 && <div className="text-muted-foreground mt-1">+ {preview.valid.length - 6} more…</div>}
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="ghost" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
                    {!preview ? (
                        <Button onClick={() => doPreview()} disabled={loading || !csv.trim()} data-testid="import-preview-btn">
                            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Preview
                        </Button>
                    ) : (
                        <Button onClick={doCommit} disabled={loading || preview.valid.length === 0} data-testid="import-commit-btn">
                            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Import {preview.valid.length} valid
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
